/**
 * Shared primitive schemas — single source of truth for cross-domain field types.
 * All domain schemas import from here; never re-define these inline.
 *
 * Conventions (non-negotiable per TYPES-1 §5):
 *   • IDs are ULIDs (26-char uppercase base32)
 *   • Dates are ISO-8601 strings
 *   • Money = { amount: integer (minor units), currency: ISO-4217 }
 */
import { z } from 'zod';

// ── Identity ─────────────────────────────────────────────────────────────────

export const UlidSchema = z
  .string()
  .regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'Must be a valid ULID');

export const TenantIdSchema = z
  .string()
  .min(1, 'tenantId is required')
  .max(128, 'tenantId too long');

// ── Temporal ─────────────────────────────────────────────────────────────────

export const IsoDateSchema = z
  .string()
  .datetime({ offset: true, message: 'Must be an ISO-8601 date-time string' });

// ── Scalar values ─────────────────────────────────────────────────────────────

export const MoneySchema = z.object({
  amount:   z.int().nonnegative('amount must be a non-negative integer (minor units)'),
  currency: z.string().length(3, 'currency must be an ISO-4217 code'),
});

export const EmailSchema = z
  .string()
  .email('Must be a valid email address');

export const PhoneSchema = z
  .string()
  .regex(/^\+[1-9]\d{6,14}$/, 'Must be E.164 format (e.g. +15551234567)');

export const UrlSchema = z
  .string()
  .url('Must be a valid URL');

export const PercentSchema = z
  .number()
  .min(0, 'Percent must be ≥ 0')
  .max(100, 'Percent must be ≤ 100');
